import { createReducer, on } from '@ngrx/store';
import { cloneDeep, keyBy } from 'lodash-es';

import * as actions from './se-buttons-toolbar.actions';
import { seButtonsToolbarInitialState, SeButtonsToolbarState } from './se-buttons-toolbar.state';
import { ToolbarAction, ToolbarButtonData, ToolbarSliderOptions, ToolbarTimelineOptions } from '../types';
import { IEstensaButtonStatus, IEstensaButtonStatusWithChildren } from '../../../models/vnext.models';

export const seButtonsToolbarReducer = createReducer(
    seButtonsToolbarInitialState,
    on(actions.seToolbarButtonEnable, (state, action) => enableButton(state, action.button, action.enabled)),
    on(actions.setToolbarButtonsEnableBulk, (state, action) => enableButtonsBulk(state, action.buttons)),
    on(actions.seToolbarButtonHide, (state, action) => hideButton(state, action.button, action.hidden)),
    on(actions.seToolbarButtonToggle, (state, action) => toggleButton(state, action.button, action.checked)),
    on(actions.seToolbarInitialize, (state, action) =>
        initialize(state, action.buttons, action.leftSlider, action.rightSlider, action.timeline),
    ),
    on(actions.seToolbarInitializeButtons, (state, action) => initializeButtons(state, action.buttons)),
    on(actions.seToolbarSetAllButtons, (state, action) => setAllButtons(state, action.buttons)),
    on(actions.seToolbarSetButton, (state, action) => setButton(state, action.action, action.button)),
    on(actions.seToolbarSetLeftSlider, (state, action) => ({ ...state, leftSlider: action.status })),
    on(actions.seToolbarSetTimeline, (state, action) => ({ ...state, timeline: action.status })),
    on(actions.seToolbarSetRightSlider, (state, action) => ({ ...state, rightSlider: action.status })),
    on(actions.seToolbarToggleLeftSlider, (state) => toggleLeftSlider(state)),
    on(actions.seToolbarToggleRightSlider, (state) => toggleRightSlider(state)),
    on(actions.seToolbarToggleTimeline, (state) => toggleTimeline(state)),
    on(actions.seToolbarSetButtonChildren, (state, action) => setButtonChildren(state, action.button, action.children)),
    on(actions.seToolbarDisableAllButtons, (state) => disableAllButtons(state)),
    on(actions.seToolbarSetTooltip, (state, action) => setTooltip(state, action.action, action.tooltip)),
);

function initializeButtons(state: SeButtonsToolbarState, buttonArr: ToolbarButtonData[]): SeButtonsToolbarState {
    const buttons = keyBy(buttonArr, 'actionType');
    return {
        ...state,
        buttons,
    };
}

function initialize(
    state: SeButtonsToolbarState,
    buttonArr: ToolbarButtonData[],
    leftSlider: ToolbarSliderOptions,
    rightSlider: ToolbarSliderOptions,
    timeline?: ToolbarTimelineOptions,
): SeButtonsToolbarState {
    const buttons = keyBy(buttonArr, 'actionType');
    return {
        ...state,
        buttons,
        leftSlider,
        rightSlider,
        timeline,
    };
}

function setAllButtons(
    state: SeButtonsToolbarState,
    newButtonState: Partial<Record<ToolbarAction, IEstensaButtonStatus | IEstensaButtonStatusWithChildren>>,
): SeButtonsToolbarState {
    const buttons = cloneDeep(state.buttons);

    for (const key in newButtonState) {
        const action = key as ToolbarAction;
        const originalButton = buttons[action];
        const newStatus = newButtonState[action];
        if (!originalButton || !newStatus) {
            continue;
        }
        
        // Create a deep copy of the button to avoid mutations
        const button = cloneDeep(originalButton);
        
        // Bottone con figli
        if ('children' in newStatus) {
            const updatedChildren = button.children?.map((child) => {
                const updatedStatus =
                    newStatus.children?.[child.actionType] ?? child.status;

                return {
                    ...child,
                    status: updatedStatus,
                };
            });

            const activeChildren =
                updatedChildren?.filter((c) => c.status.visible).length ?? 0;

            button.children = updatedChildren;

            // Only auto-disable parent button if:
            // 1. There are no active children (activeChildren === 0), AND
            // 2. The parent button was originally configured as disabled
            // This respects buttons that are meant to stay enabled regardless of children state
            const shouldAutoDisable = activeChildren === 0 && newStatus.status.disabled;

            button.status = shouldAutoDisable
                    ? {
                        visible: newStatus.status.visible,
                        disabled: true,
                        checked: newStatus.status.checked,
                    }
                    : newStatus.status;
        }
        else {
            // Bottone senza figli
            button.status = newStatus as IEstensaButtonStatus;
        }
        
        // Assign the modified copy back to buttons
        buttons[action] = button;
    }

    return {
        ...state,
        buttons,
    };
}

function setButton(
    state: SeButtonsToolbarState,
    action: ToolbarAction,
    status: Partial<ToolbarButtonData>,
): SeButtonsToolbarState {
    return {
        ...state,
        buttons: {
            ...state.buttons,
            [action]: {
                ...state.buttons[action],
                ...status,
            },
        },
    };
}

function disableAllButtons(state: SeButtonsToolbarState): SeButtonsToolbarState {
    // Buttons that should remain enabled even when "disable all" is called
    const buttonsToKeepEnabled = [
        ToolbarAction.ADMISSION_VISIT_PARENT,
        // Add other buttons here that should stay enabled during busy states
    ];

    const buttons = Object.fromEntries(
        Object.entries(state.buttons).map(([key, value]) => {
            // Check if this button should remain enabled
            const shouldKeepEnabled = buttonsToKeepEnabled.includes(key as ToolbarAction);
            
            return [key, { 
                ...value, 
                status: { 
                    ...value.status, 
                    disabled: shouldKeepEnabled ? value.status.disabled : true 
                } 
            }];
        }),
    );

    return {
        ...state,
        buttons,
    };
}

function enableButton(state: SeButtonsToolbarState, action: ToolbarAction, enabled: boolean) {
    const oldButton = state.buttons[action];
    if (!oldButton || oldButton.status.disabled === !enabled) {
        return state;
    }
    
    const newButton = cloneDeep(oldButton);
    newButton.status.disabled = !enabled;
    return {
        ...state,
        buttons: {
            ...state.buttons,
            [action]: newButton,
        },
    };
}

function enableButtonsBulk(state: SeButtonsToolbarState, buttons: { button: ToolbarAction; enabled: boolean; }[]) {
    const updatedButtons: Partial<Record<ToolbarAction, ToolbarButtonData>> = {};
    buttons.forEach((item) => {
        const oldButton = state.buttons[item.button];
        if (oldButton && oldButton.status.disabled === item.enabled) {
            updatedButtons[item.button] = {
                ...oldButton,
                status: {
                    ...oldButton.status,
                    disabled: !item.enabled,
                },
            };
        }
    });

    buttons.forEach((item) => {
        const oldButton = state.buttons[item.button];
        if (!oldButton) {
            //siamo in un children
            let child;
            const parentKey = Object.keys(state.buttons).find((buttonKey) => {
                child = state.buttons[buttonKey].children?.find(
                    (child: ToolbarButtonData) => child.actionType === item.button,
                );
                return Boolean(child);
            });

            if (parentKey && child && child.status.disabled === item.enabled) {
                if (!updatedButtons[parentKey]) {
                    updatedButtons[parentKey] = {
                        ...state.buttons[parentKey],
                        children: [],
                    };
                }
                updatedButtons[parentKey].children = state.buttons[parentKey].children?.map(
                    (child: ToolbarButtonData) =>
                        child.actionType === item.button
                            ? {
                                ...child,
                                status: { ...child.status, disabled: !item.enabled },
                            }
                            : child,
                );
            }
        }
    });

    return {
        ...state,
        buttons: {
            ...state.buttons,
            ...updatedButtons,
        },
    };
}

function hideButton(state: SeButtonsToolbarState, action: ToolbarAction, hidden: boolean) {
    const oldButton = state.buttons[action];
    if (!oldButton || oldButton.status.visible === !hidden) {
        return state;
    }
    
    // DEBUG: Log para ver qué botón se está ocultando/mostrando
    console.log(`👁️ hideButton called: ${action} -> hidden: ${hidden}`);
    
    const newButton = cloneDeep(oldButton);
    newButton.status.visible = !hidden;
    return {
        ...state,
        buttons: {
            ...state.buttons,
            [action]: newButton,
        },
    };
}

function toggleButton(state: SeButtonsToolbarState, action: ToolbarAction, checked: boolean): SeButtonsToolbarState {
    const oldButton = state.buttons[action];
    if (!oldButton || oldButton.status.checked === checked) {
        return state;
    }
    const newButton = cloneDeep(oldButton);
    newButton.status.checked = checked;
    return {
        ...state,
        buttons: {
            ...state.buttons,
            [action]: newButton,
        },
    };
}

function toggleLeftSlider(state: SeButtonsToolbarState): SeButtonsToolbarState {
    return {
        ...state,
        leftSlider: {
            ...state.leftSlider,
            checked: !state.leftSlider.checked,
        },
    };
}

function toggleRightSlider(state: SeButtonsToolbarState): SeButtonsToolbarState {
    return {
        ...state,
        rightSlider: {
            ...state.rightSlider,
            checked: !state.rightSlider.checked,
        },
    };
}

function setButtonChildren(state: SeButtonsToolbarState, action: ToolbarAction, children: ToolbarButtonData[]) {
    if (children && children.length > 0 && state.buttons[action]) {
        const oldButton = state.buttons[action];
        if (!oldButton) return state;
        const newButton = cloneDeep(oldButton);
        newButton.children = children;
        newButton.hasChildren = true;
        return {
            ...state,
            buttons: {
                ...state.buttons,
                [action]: newButton,
            },
        };
    } else {
        return state;
    }
}

function toggleTimeline(state: SeButtonsToolbarState): SeButtonsToolbarState {
    if (!state.timeline) return state;
    return {
        ...state,
        timeline: {
            ...state.timeline,
            open: !state.timeline?.open,
        },
    };
}

function setTooltip(
    state: SeButtonsToolbarState,
    action: ToolbarAction,
    tooltip: string
): SeButtonsToolbarState {
    const oldButton = state.buttons[action];
    if (!oldButton || oldButton.tooltip === tooltip) {
        return state;
    }
    const newButton = cloneDeep(oldButton);
    newButton.tooltip = tooltip;

    return {
        ...state,
        buttons: {
            ...state.buttons,
            [action]: newButton,
        },
    };
}
